import React, { useState, useContext, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  IconButton,
  Toolbar,
  Typography,
  useTheme,
  Avatar,
  Menu,
  MenuItem,
  Button,
  Container,
  Stack,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  Notifications as NotificationsIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import NotificationSystem from '../notifications/NotificationSystem';
import Logo from '../../assets/logo.png';
import useMediaQuery from '@mui/material/useMediaQuery';
import Badge from '@mui/material/Badge';
import { ExpiryNotificationContext } from '../../App';
import WasteDialog from '../items/WasteDialog';
import OnboardingTour, { isOnboardingComplete } from '../onboarding/OnboardingTour';
import OfflineIndicator from '../common/OfflineIndicator';
import { AdminErrorNotifier } from '../admin/AdminErrorAlerts';
import { isAdminUser } from '../../utils/admin';
import InsightsIcon from '@mui/icons-material/Insights';
export default function Layout() {
  const theme = useTheme();
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { expiryNotification } = useContext(ExpiryNotificationContext) || { expiryNotification: { expired: 0, expiring: 0 } };
  const [wasteDialogOpen, setWasteDialogOpen] = useState(false);
  const [wasteDialogItem, setWasteDialogItem] = useState(null);
  const [onboardingOpen, setOnboardingOpen] = useState(false);

  const handleNotificationClick = (event) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  const handleUserMenuClick = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleDrawerOpen = () => setDrawerOpen(!drawerOpen); // We Only need the one and can just alternate what its doing onClick which ive changed.

  const navItems = [
    { path: '/app/items', label: 'Items', icon: <InventoryIcon /> },
    { path: '/app/waste', label: 'Waste', icon: <DeleteIcon /> },
    { path: '/app/community', label: 'Community', icon: <PeopleIcon /> },
    ...(isAdminUser(currentUser)
      ? [{ path: '/app/admin', label: 'Soft launch', icon: <InsightsIcon /> }]
      : []),
  ];

  // Handler to open waste dialog for a given item
  const handleLogAsWaste = (item) => {
    setWasteDialogItem(item);
    setWasteDialogOpen(true);
    handleNotificationClose();
  };

  // Handler to close waste dialog
  const handleWasteDialogClose = () => {
    setWasteDialogOpen(false);
    setWasteDialogItem(null);
  };

  // Handler for after waste is logged
  const handleWasteLogged = () => {
    setWasteDialogOpen(false);
    setWasteDialogItem(null);
    // Optionally, trigger a refresh or notification here
  };

  // Helper to robustly format expiry date
  const formatExpiryDate = (expiryDate) => {
    if (!expiryDate) return 'N/A';
    let dateObj;
    if (expiryDate.toDate) {
      dateObj = expiryDate.toDate();
    } else if (expiryDate instanceof Date) {
      dateObj = expiryDate;
    } else if (typeof expiryDate === 'string' || typeof expiryDate === 'number') {
      dateObj = new Date(expiryDate);
    } else {
      return 'N/A';
    }
    if (isNaN(dateObj.getTime())) return 'N/A';
    return dateObj.toLocaleDateString();
  };

  // Check if user should see onboarding tour
  useEffect(() => {
    if (currentUser && location.pathname.startsWith('/app')) {
      // Check if user is new (created within last 24 hours) and hasn't completed onboarding
      const isNewUser = currentUser.profile?.createdAt &&
        (new Date() - new Date(currentUser.profile.createdAt)) < 24 * 60 * 60 * 1000;

      const hasCompletedOnboarding = isOnboardingComplete();

      // Show tour for new users who haven't completed it
      if (isNewUser && !hasCompletedOnboarding && !onboardingOpen) {
        // Small delay to let the page load
        const timer = setTimeout(() => {
          setOnboardingOpen(true);
          navigate('/app/items'); // Start at items page
        }, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [currentUser, location.pathname, navigate, onboardingOpen]);

  // Listen for manual tour trigger (e.g., from Profile page)
  useEffect(() => {
    const handleStartTour = () => {
      if (currentUser && !onboardingOpen) {
        setOnboardingOpen(true);
        navigate('/app/items'); // Start at items page
      }
    };

    window.addEventListener('startOnboardingTour', handleStartTour);
    return () => {
      window.removeEventListener('startOnboardingTour', handleStartTour);
    };
  }, [currentUser, navigate, onboardingOpen]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, width: '100%' }}>
        <Toolbar sx={{ width: '100%', justifyContent: { xs: 'center', sm: 'space-between' }, flexWrap: 'wrap', p: 0, paddingX: isMobile && '1rem' }}>
          <Stack
            direction={{ xs: 'row', sm: 'row' }}
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            sx={{ width: '100%' }}
          >
            {/* Logo and Brand */}
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 120, justifyContent: { xs: 'flex-start', sm: 'flex-start' }, width: { xs: 'auto', sm: 'auto' } }}>
              {isMobile && (
                <IconButton color="inherit" edge="start" onClick={handleDrawerOpen} sx={{ mr: 1 }}>
                  <MenuIcon />
                </IconButton>
              )}
              <Typography
                variant="h6"
                component="div"
                sx={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  fontWeight: 'bold',
                  letterSpacing: '0.5px'
                }}
                onClick={() => handleNavigation('/app/items')}
              >
                <img
                  src={Logo}
                  alt="NaeWaste Logo"
                  style={{ width: 40, marginRight: 16 }}
                />
                NaeWASTE
              </Typography>
            </Box>

            {/* Navigation Items - Top bar for sm and up only */}
            {!isMobile && (
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  alignItems: 'center',
                  mt: 0
                }}
              >
                {navItems.map((item) => (
                  <Button
                    key={item.path}
                    color="inherit"
                    startIcon={item.icon}
                    onClick={() => handleNavigation(item.path)}
                    sx={{
                      backgroundColor: location.pathname === item.path ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      },
                      minWidth: '100px',
                      justifyContent: 'center'
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Stack>
            )}

            {/* Profile/Avatar and Notifications */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 80, justifyContent: { xs: 'center', sm: 'flex-end' }, width: { xs: 'auto', sm: 'auto' } }}>
              <IconButton color="inherit" onClick={handleNotificationClick}>
                <Badge color="error" badgeContent={expiryNotification.expired + expiryNotification.expiring} invisible={expiryNotification.expired + expiryNotification.expiring === 0}>
                  <NotificationsIcon />
                </Badge>
              </IconButton>
              <IconButton
                onClick={handleUserMenuClick}
                sx={{ p: 0 }}
              >
                {currentUser?.profile?.avatarSVG ? (
                  <Avatar sx={{ width: 32, height: 32 }}>
                    <span
                      dangerouslySetInnerHTML={{ __html: currentUser.profile.avatarSVG }}
                      style={{ display: 'block', width: 32, height: 32 }}
                    />
                  </Avatar>
                ) : (
                  <Avatar
                    alt={currentUser?.displayName || 'User'}
                    src={currentUser?.photoURL}
                    sx={{ width: 32, height: 32 }}
                  />
                )}
              </IconButton>
            </Box>
          </Stack>
        </Toolbar>
      </AppBar>

      {/* Drawer for mobile navigation */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={handleDrawerOpen}
        ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: 'block', sm: 'none' } }}
      >
        {/* [TRACKING] Drawer now uses width: 100vw for full-width on mobile. See changes_tracking.md. */}
        {/* [ROLLBACK] Revert Drawer Box to width: 220 if needed. */}
        <Box
          sx={{
            width: { xs: '100vw', sm: 220 },
            pt: { xs: '56px', sm: '64px' },
            bgcolor: 'background.paper',
            height: '100%',
          }}
          role="presentation"
          onClick={handleDrawerOpen}
        >
          <List>
            {navItems.map((item) => (
              <ListItem key={item.path} disablePadding>
                <ListItemButton onClick={() => handleNavigation(item.path)} selected={location.pathname === item.path}>
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          mt: 8, // Add margin top to account for fixed AppBar
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Container maxWidth="xl" disableGutters>
          <Outlet />
        </Container>
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 2,
          px: 3,
          mt: 'auto',
          backgroundColor: theme.palette.background.paper,
          borderTop: `1px solid ${theme.palette.divider}`,
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          © {new Date().getFullYear()} NaeWaste. All rights reserved. |{' '}
          <Link
            to={currentUser ? "/app/privacy" : "/privacy"}
            style={{
              color: theme.palette.primary.main,
              textDecoration: 'none',
            }}
          >
            Privacy Policy
          </Link>
          {' | '}
          <Link
            to={currentUser ? "/app/terms" : "/terms"}
            style={{
              color: theme.palette.primary.main,
              textDecoration: 'none',
            }}
          >
            Terms of Service
          </Link>
          {' | '}
          <Link
            to={currentUser ? "/app/contact" : "/contact"}
            style={{
              color: theme.palette.primary.main,
              textDecoration: 'none',
            }}
          >
            Contact
          </Link>
          {' | '}
          <Link
            to={currentUser ? "/app/help" : "/help"}
            style={{
              color: theme.palette.primary.main,
              textDecoration: 'none',
            }}
          >
            Help
          </Link>
        </Typography>
      </Box>

      {/* User Menu */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
      >
        <MenuItem onClick={() => {
          handleUserMenuClose();
          handleNavigation('/app/profile');
        }}>
          <PersonIcon sx={{ mr: 1 }} />
          Profile
        </MenuItem>
        <MenuItem onClick={() => {
          handleUserMenuClose();
          handleNavigation('/app/privacy');
        }}>
          Privacy Policy
        </MenuItem>
        <MenuItem onClick={() => {
          handleUserMenuClose();
          handleNavigation('/app/terms');
        }}>
          Terms of Service
        </MenuItem>
        <MenuItem onClick={() => {
          handleUserMenuClose();
          handleNavigation('/app/contact');
        }}>
          Contact Support
        </MenuItem>
        <MenuItem onClick={() => {
          handleUserMenuClose();
          handleNavigation('/app/help');
        }}>
          Help & FAQ
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          Logout
        </MenuItem>
      </Menu>

      {/* Notification Menu */}
      <Menu
        anchorEl={notificationAnchor}
        open={Boolean(notificationAnchor)}
        onClose={handleNotificationClose}
      >
        {expiryNotification.expired === 0 && expiryNotification.expiring === 0 ? (
          <MenuItem onClick={handleNotificationClose}>
            No new notifications
          </MenuItem>
        ) : (
          <Box sx={{ minWidth: 280, maxWidth: 350, p: 1 }}>
            {expiryNotification.expired > 0 && (
              <>
                <Typography variant="subtitle2" color="error" sx={{ pl: 2, pt: 1 }}>
                  Expired Items
                </Typography>
                {expiryNotification.expiredItems.map((item) => (
                  <MenuItem key={item.id || item.name} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">{item.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.quantity} {item.unit} &middot; Expired: {formatExpiryDate(item.expiryDate)}
                      </Typography>
                    </Box>
                    <Button size="small" color="warning" variant="outlined" sx={{ ml: 1 }} onClick={() => handleLogAsWaste(item)}>
                      Log as Waste
                    </Button>
                  </MenuItem>
                ))}
              </>
            )}
            {expiryNotification.expiring > 0 && (
              <>
                <Typography variant="subtitle2" color="warning.main" sx={{ pl: 2, pt: 1 }}>
                  Expiring Soon
                </Typography>
                {expiryNotification.expiringItems.map((item) => (
                  <MenuItem key={item.id || item.name} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">{item.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.quantity} {item.unit} &middot; Expires: {formatExpiryDate(item.expiryDate)}
                      </Typography>
                    </Box>
                    <Button size="small" color="warning" variant="outlined" sx={{ ml: 1 }} onClick={() => handleLogAsWaste(item)}>
                      Log as Waste
                    </Button>
                  </MenuItem>
                ))}
              </>
            )}
          </Box>
        )}
      </Menu>

      {/* Waste Dialog (from notification popover) */}
      {wasteDialogOpen && wasteDialogItem && (
        <WasteDialog
          open={wasteDialogOpen}
          item={wasteDialogItem}
          onClose={handleWasteDialogClose}
          onWasteLogged={handleWasteLogged}
          currentUser={currentUser}
        />
      )}

      <NotificationSystem />
      <AdminErrorNotifier />

      {/* Offline Indicator */}
      <OfflineIndicator />

      {/* Onboarding Tour */}
      {currentUser && (
        <OnboardingTour
          open={onboardingOpen}
          onClose={() => setOnboardingOpen(false)}
        />
      )}
    </Box>
  );
} 